/**
 * StateGovSearchLinksPage — Reference · State Gov Search Links.
 *
 * The external Secretary-of-State / court-records lookup URLs the people form's
 * Lookup button opens (Teams `app.openLink()`). Composite natural key =
 * (stateCode, searchType). Backs `/v3/config/state-gov-search-links`.
 *
 * Contract (verified against the backend 2026-06-16 — entity / DTO / config
 * read / admin upsert all agree on field names; NO drift):
 *   - Entity:  StateGovSearchLink.cs (StateCode, SearchType, SearchUrl,
 *              DisplayLabel, Notes, IsActive, LastValidated + audit cols).
 *   - Read:    GET /v3/config/state-gov-search-links → { version, links }
 *              (typed: client.retail.config.stateGovSearchLinks.list(); returns
 *              StateGovSearchLink[] with the exact camelCase wire fields).
 *              ⚠ The route filters `WHERE r.IsActive`, so inactive rows are
 *              NOT returned by this surface — there is no reactivation path.
 *   - Upsert:  POST /v3/admin/state-gov-search-links (AdminReferenceService;
 *              natural key (StateCode, SearchType); persists every field —
 *              no silent drop). Typed: …admin.reference.stateGovSearchLinks
 *              .upsert(SENTINEL, body).
 *   - Delete:  DELETE /v3/admin/state-gov-search-links/{stateCode}/{searchType}
 *              = HARD delete (EF `.Remove()`). Typed: …remove(SENTINEL, …).
 *
 * Delete choice = HARD delete. Because the config read filters on IsActive, a
 * soft-delete (IsActive=false) would vanish the row from every surface — the
 * client list AND the people-form Lookup — with no UI path to bring it back.
 * A real DELETE is therefore the honest operation here. (The source ROP-teams
 * editor "soft-deleted" then disabled its own Delete button, which produced the
 * same one-way disappearance but left an orphaned inactive row in SQL.)
 *
 * Migrated 2026-06-16 from ROP-teams Admin (operational app writes no config).
 * Auth: ADMIN_AUTH_TOKEN_SENTINEL (MSAL bearer; no secret in the bundle).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ADMIN_AUTH_TOKEN_SENTINEL,
  ApiError,
} from '@retail-ops/api-client';
import type {
  StateGovSearchLink,
  StateGovSearchType,
} from '@retail-ops/shared-types';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { TextField, SelectField, TextArea, BoolField } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';

// ───────────────────────────────────────────────────────────────────────────
// Constants
// ───────────────────────────────────────────────────────────────────────────

const SEARCH_TYPES: ReadonlyArray<StateGovSearchType> = ['Business', 'CourtRecords'];

const SEARCH_TYPE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'Business', label: 'Business (Secretary of State)' },
  { value: 'CourtRecords', label: 'Court Records' },
];

/** USPS 2-char codes for all 50 states (alpha order; DC excluded per source spec). */
const US_STATE_CODES: ReadonlyArray<string> = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

/**
 * Best-effort default Secretary-of-State / Division-of-Corporations business
 * entity search URLs for every US state (sourced 2026-04 from each state's
 * public SoS portal). Intentionally admin-editable — the 50-state bulk import
 * scaffolds rows instantly for later curation; officers refine per state.
 */
const STATE_DEFAULT_BUSINESS_URLS: Record<string, { url: string; label: string }> = {
  AL: { url: 'https://arc-sos.state.al.us/CGI/CORPNAME.MBR/INPUT', label: 'Alabama SoS Business Entity Search' },
  AK: { url: 'https://www.commerce.alaska.gov/cbp/main/search/entities', label: 'Alaska Div of Banking & Securities Entity Search' },
  AZ: { url: 'https://ecorp.azcc.gov/EntitySearch/Index', label: 'Arizona Corporation Commission eCorp Search' },
  AR: { url: 'https://www.sos.arkansas.gov/corps/search_corps.php', label: 'Arkansas SoS Corporation Search' },
  CA: { url: 'https://bizfileonline.sos.ca.gov/search/business', label: 'California SoS Business Search' },
  CO: { url: 'https://www.coloradosos.gov/biz/BusinessEntityCriteriaExt.do', label: 'Colorado SoS Business Database' },
  CT: { url: 'https://service.ct.gov/business/s/onlinebusinesssearch', label: 'Connecticut SoS Business Search' },
  DE: { url: 'https://icis.corp.delaware.gov/ecorp/entitysearch/NameSearch.aspx', label: 'Delaware Div of Corporations Entity Search' },
  FL: { url: 'https://search.sunbiz.org/Inquiry/CorporationSearch/ByName', label: 'Florida Sunbiz Entity Search' },
  GA: { url: 'https://ecorp.sos.ga.gov/BusinessSearch', label: 'Georgia SoS Business Search' },
  HI: { url: 'https://hbe.ehawaii.gov/documents/search.html', label: 'Hawaii Business Express Entity Search' },
  ID: { url: 'https://sosbiz.idaho.gov/search/business', label: 'Idaho SoS Business Search' },
  IL: { url: 'https://apps.ilsos.gov/corporatellc/', label: 'Illinois SoS Corporation/LLC Search' },
  IN: { url: 'https://bsd.sos.in.gov/PublicBusinessSearch', label: 'Indiana SoS Business Search' },
  IA: { url: 'https://sos.iowa.gov/search/business/search.aspx', label: 'Iowa SoS Business Entities Search' },
  KS: { url: 'https://www.sos.ks.gov/eforms/BusinessEntity/Search.aspx', label: 'Kansas SoS Business Entity Search' },
  KY: { url: 'https://web.sos.ky.gov/ftsearch/', label: 'Kentucky SoS Business Entity Search' },
  LA: { url: 'https://coraweb.sos.la.gov/CommercialSearch/CommercialSearch.aspx', label: 'Louisiana SoS Commercial Search' },
  ME: { url: 'https://icrs.informe.org/nei-sos-icrs/ICRS', label: 'Maine SoS Corporate Name Search' },
  MD: { url: 'https://egov.maryland.gov/BusinessExpress/EntitySearch', label: 'Maryland Business Express Entity Search' },
  MA: { url: 'https://corp.sec.state.ma.us/CorpWeb/CorpSearch/CorpSearch.aspx', label: 'Massachusetts SoC Corporate Search' },
  MI: { url: 'https://cofs.lara.state.mi.us/SearchApi/Search/Search', label: 'Michigan LARA Corporate Entity Search' },
  MN: { url: 'https://mblsportal.sos.state.mn.us/Business/Search', label: 'Minnesota SoS Business Search' },
  MS: { url: 'https://corp.sos.ms.gov/corp/portal/c/page/corpbusinessidsearch/portal.aspx', label: 'Mississippi SoS Business Search' },
  MO: { url: 'https://bsd.sos.mo.gov/BusinessEntity/BESearch.aspx?SearchType=0', label: 'Missouri SoS Business Entity Search' },
  MT: { url: 'https://biz.sosmt.gov/search/business', label: 'Montana SoS Business Search' },
  NE: { url: 'https://www.nebraska.gov/sos/corp/corpsearch.cgi', label: 'Nebraska SoS Corporate Search' },
  NV: { url: 'https://esos.nv.gov/EntitySearch/OnlineEntitySearch', label: 'Nevada SoS Entity Search' },
  NH: { url: 'https://quickstart.sos.nh.gov/online/BusinessInquire', label: 'New Hampshire SoS QuickStart Business Inquiry' },
  NJ: { url: 'https://www.njportal.com/DOR/BusinessNameSearch/Search/Availability', label: 'New Jersey DoR Business Name Search' },
  NM: { url: 'https://portal.sos.state.nm.us/BFS/online/CorporationBusinessSearch', label: 'New Mexico SoS Corporation Search' },
  NY: { url: 'https://apps.dos.ny.gov/publicInquiry/', label: 'New York DoS Corporation & Business Entity Search' },
  NC: { url: 'https://www.sosnc.gov/online_services/search/by_title/_Business_Registration', label: 'North Carolina SoS Business Registration Search' },
  ND: { url: 'https://firststop.sos.nd.gov/search/business', label: 'North Dakota SoS FirstStop Business Search' },
  OH: { url: 'https://businesssearch.ohiosos.gov/', label: 'Ohio SoS Business Search' },
  OK: { url: 'https://www.sos.ok.gov/corp/corpInquiryFind.aspx', label: 'Oklahoma SoS Corporate Search' },
  OR: { url: 'https://sos.oregon.gov/business/Pages/find.aspx', label: 'Oregon SoS Business Search' },
  PA: { url: 'https://file.dos.pa.gov/search/business', label: 'Pennsylvania DoS Business Search' },
  RI: { url: 'https://business.sos.ri.gov/corpweb/corpsearch/corpsearch.aspx', label: 'Rhode Island SoS Corporate Database' },
  SC: { url: 'https://businessfilings.sc.gov/BusinessFiling/Entity/Search', label: 'South Carolina SoS Business Entity Search' },
  SD: { url: 'https://sosenterprise.sd.gov/BusinessServices/Business/FilingSearch.aspx', label: 'South Dakota SoS Business Filing Search' },
  TN: { url: 'https://tnbear.tn.gov/Ecommerce/FilingSearch.aspx', label: 'Tennessee SoS TNBear Filing Search' },
  TX: { url: 'https://mycpa.cpa.state.tx.us/coa/', label: 'Texas Comptroller Taxable Entity Search' },
  UT: { url: 'https://secure.utah.gov/bes/', label: 'Utah Div of Corporations Business Entity Search' },
  VT: { url: 'https://bizfilings.vermont.gov/online/BusinessInquire/', label: 'Vermont SoS Business Inquiry' },
  VA: { url: 'https://cis.scc.virginia.gov/EntitySearch/Index', label: 'Virginia SCC Business Entity Search' },
  WA: { url: 'https://ccfs.sos.wa.gov/#/', label: 'Washington SoS Corporations & Charities Search' },
  WV: { url: 'https://apps.sos.wv.gov/business/corporations/', label: 'West Virginia SoS Business Entity Search' },
  WI: { url: 'https://www.wdfi.org/apps/CorpSearch/Search.aspx', label: 'Wisconsin DFI Corporate Records Search' },
  WY: { url: 'https://wyobiz.wyo.gov/Business/FilingSearch.aspx', label: 'Wyoming SoS Business Filing Search' },
};

// ───────────────────────────────────────────────────────────────────────────
// Helpers
// ───────────────────────────────────────────────────────────────────────────

function formatRelative(iso: string | null | undefined): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso ?? '—';
  const absMs = Math.abs(Date.now() - then);
  const day = 24 * 60 * 60 * 1000;
  if (absMs < day) return 'today';
  const days = Math.round(absMs / day);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  if (days < 30) {
    const weeks = Math.round(days / 7);
    return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
  }
  if (days < 365) {
    const months = Math.round(days / 30);
    return `${months} month${months === 1 ? '' : 's'} ago`;
  }
  const years = Math.round(days / 365);
  return `${years} year${years === 1 ? '' : 's'} ago`;
}

function isValidUrl(raw: string): boolean {
  try {
    const u = new URL(raw.trim());
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

// ───────────────────────────────────────────────────────────────────────────
// Edit-draft shape
// ───────────────────────────────────────────────────────────────────────────

interface EditDraft {
  stateCode: string;
  searchType: StateGovSearchType;
  displayLabel: string;
  searchUrl: string;
  notes: string;
  isActive: boolean;
  lastValidated: string; // yyyy-mm-dd or ''
  isNew: boolean;
}

function emptyDraft(): EditDraft {
  return {
    stateCode: '',
    searchType: 'Business',
    displayLabel: '',
    searchUrl: '',
    notes: '',
    isActive: true,
    lastValidated: '',
    isNew: true,
  };
}

function rowToDraft(row: StateGovSearchLink): EditDraft {
  return {
    stateCode: row.stateCode,
    searchType: row.searchType,
    displayLabel: row.displayLabel,
    searchUrl: row.searchUrl,
    notes: row.notes ?? '',
    isActive: row.isActive,
    lastValidated: row.lastValidated ? row.lastValidated.slice(0, 10) : '',
    isNew: false,
  };
}

// ───────────────────────────────────────────────────────────────────────────
// Component
// ───────────────────────────────────────────────────────────────────────────

export function StateGovSearchLinksPage(): JSX.Element {
  const client = useApiClient();

  const [rows, setRows] = useState<ReadonlyArray<StateGovSearchLink> | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<StateGovSearchLink | null>(null);

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkError, setBulkError] = useState<string | null>(null);

  const [toast, setToast] = useState<{ tone: 'info' | 'warn'; text: string } | null>(null);

  // ── Load ──────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setRows(null);
      setLoadError(null);
      try {
        const resp = await client.retail.config.stateGovSearchLinks.list();
        if (cancelled) return;
        if (resp.success && resp.data) {
          setRows(resp.data.links);
        } else {
          setLoadError(resp.error ?? 'Failed to load state-gov search links.');
        }
      } catch (e) {
        if (cancelled) return;
        setLoadError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, refreshNonce]);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  // ── Derived ───────────────────────────────────────────────────────────
  const stateCodesPresent = useMemo(() => {
    const seen = new Set<string>();
    for (const r of rows ?? []) seen.add(r.stateCode);
    return [...seen].sort();
  }, [rows]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const sorted = (rows ?? []).slice().sort((a, b) => {
      const sc = a.stateCode.localeCompare(b.stateCode);
      return sc !== 0 ? sc : a.searchType.localeCompare(b.searchType);
    });
    return sorted.filter((r) => {
      if (stateFilter && r.stateCode !== stateFilter) return false;
      if (typeFilter && r.searchType !== typeFilter) return false;
      if (!needle) return true;
      return (
        r.stateCode.toLowerCase().includes(needle) ||
        r.displayLabel.toLowerCase().includes(needle) ||
        r.searchUrl.toLowerCase().includes(needle)
      );
    });
  }, [rows, search, stateFilter, typeFilter]);

  // ── Upsert (single row) ───────────────────────────────────────────────
  const upsertOne = useCallback(
    async (d: {
      stateCode: string;
      searchType: StateGovSearchType;
      displayLabel: string;
      searchUrl: string;
      notes: string | null;
      isActive: boolean;
      lastValidated: string | null;
    }): Promise<void> => {
      const resp = await client.retail.admin.reference.stateGovSearchLinks.upsert(
        ADMIN_AUTH_TOKEN_SENTINEL,
        {
          stateCode: d.stateCode.toUpperCase(),
          searchType: d.searchType,
          searchUrl: d.searchUrl.trim(),
          displayLabel: d.displayLabel.trim(),
          notes: d.notes,
          isActive: d.isActive,
          lastValidated: d.lastValidated,
        },
      );
      if (!resp.success) throw new Error(resp.error ?? 'admin upsert returned success=false');
    },
    [client],
  );

  const handleSave = useCallback(async () => {
    if (!draft) return;
    const code = draft.stateCode.trim();
    if (!/^[A-Za-z]{2}$/.test(code)) {
      setSaveError('State code must be exactly 2 letters.');
      return;
    }
    if (!draft.displayLabel.trim()) {
      setSaveError('Display label is required.');
      return;
    }
    if (!isValidUrl(draft.searchUrl)) {
      setSaveError('Search URL must be a valid http(s) URL.');
      return;
    }
    if (draft.isNew) {
      const collision = (rows ?? []).some(
        (r) => r.stateCode === code.toUpperCase() && r.searchType === draft.searchType,
      );
      if (collision) {
        setSaveError(`A row already exists for ${code.toUpperCase()} / ${draft.searchType}. Edit it instead.`);
        return;
      }
    }
    setSaving(true);
    setSaveError(null);
    try {
      await upsertOne({
        stateCode: code,
        searchType: draft.searchType,
        displayLabel: draft.displayLabel,
        searchUrl: draft.searchUrl,
        notes: draft.notes.trim() === '' ? null : draft.notes.trim(),
        isActive: draft.isActive,
        lastValidated: draft.lastValidated === '' ? null : draft.lastValidated,
      });
      const label = `${code.toUpperCase()} / ${draft.searchType}`;
      setDraft(null);
      setToast({
        tone: draft.isActive ? 'info' : 'warn',
        text: draft.isActive
          ? `Saved. ${label} is updated.`
          : `Saved. ${label} is now inactive — it will drop off this list (the config read only returns active links).`,
      });
      refresh();
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [draft, rows, upsertOne, refresh]);

  // ── Hard delete ───────────────────────────────────────────────────────
  const handleHardDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setSaving(true);
    setSaveError(null);
    try {
      const resp = await client.retail.admin.reference.stateGovSearchLinks.remove(
        ADMIN_AUTH_TOKEN_SENTINEL,
        deleteTarget.stateCode,
        deleteTarget.searchType,
      );
      if (!resp.success) throw new Error(resp.error ?? 'admin delete returned success=false');
      setToast({
        tone: 'warn',
        text: `Deleted ${deleteTarget.stateCode} / ${deleteTarget.searchType}.`,
      });
      setDeleteTarget(null);
      refresh();
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [deleteTarget, client, refresh]);

  // ── 50-state bulk import ──────────────────────────────────────────────
  const openBulk = useCallback(() => {
    const existingBizStates = new Set(
      (rows ?? []).filter((r) => r.searchType === 'Business').map((r) => r.stateCode),
    );
    const preset = US_STATE_CODES.filter((sc) => !existingBizStates.has(sc))
      .map((sc) => {
        const def = STATE_DEFAULT_BUSINESS_URLS[sc];
        return def ? `${sc},Business,${def.url},${def.label}` : null;
      })
      .filter((x): x is string => x !== null)
      .join('\n');
    setBulkText(
      preset === ''
        ? '# All 50 states already have Business rows. Add custom rows one per line:\n# StateCode,SearchType,SearchUrl,DisplayLabel'
        : preset,
    );
    setBulkError(null);
    setBulkOpen(true);
  }, [rows]);

  const parseBulk = useCallback(
    (text: string): Array<{
      stateCode: string;
      searchType: StateGovSearchType;
      searchUrl: string;
      displayLabel: string;
    }> | string => {
      const out: Array<{
        stateCode: string;
        searchType: StateGovSearchType;
        searchUrl: string;
        displayLabel: string;
      }> = [];
      const lines = text.split(/\r?\n/);
      for (let i = 0; i < lines.length; i++) {
        const rawLine = lines[i]?.trim() ?? '';
        if (rawLine === '' || rawLine.startsWith('#')) continue;
        // Split on the first 3 commas only so URL/label may contain commas.
        const parts: string[] = [];
        let cursor = rawLine;
        for (let p = 0; p < 3; p++) {
          const idx = cursor.indexOf(',');
          if (idx === -1) return `Line ${i + 1}: expected 4 comma-separated fields.`;
          parts.push(cursor.slice(0, idx).trim());
          cursor = cursor.slice(idx + 1);
        }
        parts.push(cursor.trim());
        const [stateCode, typeRaw, url, label] = parts;
        if (!stateCode || !/^[A-Za-z]{2}$/.test(stateCode)) {
          return `Line ${i + 1}: bad state code "${stateCode ?? ''}".`;
        }
        if (typeRaw !== 'Business' && typeRaw !== 'CourtRecords') {
          return `Line ${i + 1}: search type must be Business or CourtRecords.`;
        }
        if (!url || !isValidUrl(url)) return `Line ${i + 1}: invalid or missing http(s) URL.`;
        if (!label) return `Line ${i + 1}: missing display label.`;
        out.push({
          stateCode: stateCode.toUpperCase(),
          searchType: typeRaw,
          searchUrl: url,
          displayLabel: label,
        });
      }
      if (out.length === 0) return 'No rows parsed — the textarea was empty or all lines were comments.';
      return out;
    },
    [],
  );

  const handleBulkImport = useCallback(async () => {
    const parsed = parseBulk(bulkText);
    if (typeof parsed === 'string') {
      setBulkError(parsed);
      return;
    }
    setSaving(true);
    setBulkError(null);
    try {
      // Fail-fast loop — one typed upsert per row.
      for (const r of parsed) {
        await upsertOne({
          stateCode: r.stateCode,
          searchType: r.searchType,
          displayLabel: r.displayLabel,
          searchUrl: r.searchUrl,
          notes: null,
          isActive: true,
          lastValidated: null,
        });
      }
      setBulkOpen(false);
      setToast({ tone: 'info', text: `Imported ${parsed.length} row${parsed.length === 1 ? '' : 's'}.` });
      refresh();
    } catch (e) {
      setBulkError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [bulkText, parseBulk, upsertOne, refresh]);

  // ── Columns ───────────────────────────────────────────────────────────
  const columns: ReadonlyArray<DataTableColumn<StateGovSearchLink>> = useMemo(
    () => [
      {
        header: 'State',
        width: 70,
        cell: (r) => <code className="cell-mono--bold num">{r.stateCode}</code>,
        sortKey: (r) => r.stateCode,
      },
      {
        header: 'Type',
        width: 150,
        cell: (r) => (
          <Pill tone={r.searchType === 'Business' ? 'info' : 'muted'}>
            {r.searchType === 'Business' ? 'Business' : 'Court Records'}
          </Pill>
        ),
        sortKey: (r) => r.searchType,
      },
      {
        header: 'Display label',
        cell: (r) => <span className="cell-strong">{r.displayLabel}</span>,
        sortKey: (r) => r.displayLabel,
      },
      {
        header: 'URL',
        cell: (r) => (
          <a
            href={r.searchUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="cell-mono"
            title={r.searchUrl}
          >
            {r.searchUrl}
          </a>
        ),
      },
      {
        header: 'Validated',
        width: 130,
        cell: (r) => <span className="cell-desc">{formatRelative(r.lastValidated)}</span>,
        sortKey: (r) => r.lastValidated ?? null,
      },
      {
        header: '',
        cell: (r) => (
          <div className="card-actions-row">
            <Button onClick={() => { setDraft(rowToDraft(r)); setSaveError(null); }}>Edit</Button>
            <Button variant="danger" onClick={() => { setDeleteTarget(r); setSaveError(null); }}>
              Delete
            </Button>
          </div>
        ),
      },
    ],
    [],
  );

  // ── Render ────────────────────────────────────────────────────────────
  if (loadError) {
    return (
      <Banner tone="err">
        Could not load state-gov search links: {loadError} <Button onClick={refresh}>Retry</Button>
      </Banner>
    );
  }
  if (!rows) return <Spinner label="Loading state-gov search links…" />;

  return (
    <>
      {toast && (
        <Banner tone={toast.tone}>
          {toast.text} <Button onClick={() => setToast(null)}>Dismiss</Button>
        </Banner>
      )}

      <Card
        title={`State Gov Search Links — ${visible.length} of ${rows.length}`}
        actions={
          <div className="card-actions-row">
            <div className="users-filter">
              <TextField label="" value={search} onChange={setSearch} placeholder="state, label, or URL…" />
            </div>
            <SelectField
              label=""
              value={stateFilter}
              onChange={setStateFilter}
              placeholder="All states"
              options={stateCodesPresent.map((sc) => ({ value: sc, label: sc }))}
            />
            <SelectField
              label=""
              value={typeFilter}
              onChange={setTypeFilter}
              placeholder="All types"
              options={SEARCH_TYPE_OPTIONS}
            />
            <Button onClick={openBulk}>50-State Bulk Import</Button>
            <Button variant="primary" onClick={() => { setDraft(emptyDraft()); setSaveError(null); }}>
              + Add Link
            </Button>
          </div>
        }
      >
        <p className="cell-desc">
          External Secretary-of-State / court-records URLs the people-form Lookup button opens. Natural
          key = (state, type). Only active links are returned by the live config read, so a deleted link
          disappears everywhere — delete is permanent.
        </p>
        <DataTable<StateGovSearchLink>
          columns={columns}
          rows={visible}
          rowKey={(r) => `${r.stateCode}|${r.searchType}`}
          emptyState={
            <p className="empty">
              No links match the current filters. Add one or run the 50-State Bulk Import to seed the catalog.
            </p>
          }
        />
      </Card>

      {/* ── Add / Edit modal ───────────────────────────────────────────── */}
      <ModalShell
        open={draft !== null}
        title={draft?.isNew ? 'Add a state-gov search link' : `Edit ${draft?.stateCode ?? ''} / ${draft?.searchType ?? ''}`}
        onClose={() => {
          if (saving) return;
          setDraft(null);
          setSaveError(null);
        }}
        width={680}
        actions={
          draft && (
            <>
              <Button onClick={() => setDraft(null)} disabled={saving}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => void handleSave()} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </>
          )
        }
      >
        {draft && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}

            <div className="form-row">
              <TextField
                label="State code"
                value={draft.stateCode}
                onChange={(v) => draft.isNew && setDraft({ ...draft, stateCode: v.toUpperCase().slice(0, 2) })}
                disabled={!draft.isNew}
                monospace
                maxLength={2}
                placeholder="MO"
                hint={draft.isNew ? '2-letter USPS code. Permanent.' : 'Permanent.'}
                required
              />
              <SelectField
                label="Search type"
                value={draft.searchType}
                onChange={(v) => draft.isNew && setDraft({ ...draft, searchType: v as StateGovSearchType })}
                options={SEARCH_TYPE_OPTIONS}
                disabled={!draft.isNew}
                hint={draft.isNew ? 'Part of the natural key. Permanent.' : 'Permanent.'}
                required
              />
            </div>

            <TextField
              label="Display label"
              value={draft.displayLabel}
              onChange={(v) => setDraft({ ...draft, displayLabel: v })}
              maxLength={128}
              placeholder="e.g. Missouri SoS Business Entity Search"
              required
            />

            <TextField
              label="Search URL"
              value={draft.searchUrl}
              onChange={(v) => setDraft({ ...draft, searchUrl: v })}
              maxLength={1024}
              monospace
              placeholder="https://…"
              hint="Opened by the people-form Lookup button. Must be a valid http(s) URL."
              required
            />

            <TextArea
              label="Notes (optional)"
              value={draft.notes}
              onChange={(v) => setDraft({ ...draft, notes: v })}
              rows={2}
              placeholder="Internal notes — e.g. requires CAPTCHA, last reviewed by…"
            />

            <div className="form-row">
              <TextField
                label="Last validated (YYYY-MM-DD)"
                value={draft.lastValidated}
                onChange={(v) => setDraft({ ...draft, lastValidated: v })}
                placeholder="2026-04-01"
                monospace
                hint="ISO date of the last manual URL check. Leave blank if never."
              />
              <div />
            </div>

            <BoolField
              label="Active"
              value={draft.isActive}
              onChange={(v) => setDraft({ ...draft, isActive: v })}
              hint="Inactive links are excluded from the live config read and drop off this list."
            />
          </div>
        )}
      </ModalShell>

      {/* ── 50-State bulk import modal ─────────────────────────────────── */}
      <ModalShell
        open={bulkOpen}
        title="50-State Bulk Import — Business searches"
        onClose={() => {
          if (saving) return;
          setBulkOpen(false);
          setBulkError(null);
        }}
        width={820}
        actions={
          <>
            <Button onClick={() => setBulkOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => void handleBulkImport()} disabled={saving}>
              {saving ? 'Importing…' : 'Accept & Import'}
            </Button>
          </>
        }
      >
        <div className="form-stack">
          <Banner tone="info">
            One row per line: <code className="cell-mono">StateCode,SearchType,SearchUrl,DisplayLabel</code>.
            Lines starting with <code className="cell-mono">#</code> are ignored. The presets are
            admin-editable starting points — refine each URL per state after import.
          </Banner>
          {bulkError && <Banner tone="err">{bulkError}</Banner>}
          <TextArea
            label="Rows"
            value={bulkText}
            onChange={setBulkText}
            rows={18}
          />
        </div>
      </ModalShell>

      {/* ── Hard-delete confirm modal ──────────────────────────────────── */}
      <ModalShell
        open={deleteTarget !== null}
        title="Delete this search link?"
        onClose={() => {
          if (saving) return;
          setDeleteTarget(null);
          setSaveError(null);
        }}
        width={520}
        actions={
          deleteTarget && (
            <>
              <Button onClick={() => setDeleteTarget(null)} disabled={saving}>
                Keep it
              </Button>
              <Button variant="danger" onClick={() => void handleHardDelete()} disabled={saving}>
                {saving ? 'Deleting…' : 'Delete permanently'}
              </Button>
            </>
          )
        }
      >
        {deleteTarget && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}
            <p className="modal-prose">
              Permanently delete the lookup link for{' '}
              <strong>{deleteTarget.displayLabel}</strong> (
              <code className="cell-mono">{deleteTarget.stateCode} / {deleteTarget.searchType}</code>)?
              This removes the row from SQL — the people-form Lookup button for this state/type will no
              longer open it. There is no undo; re-add it manually if needed.
            </p>
          </div>
        )}
      </ModalShell>
    </>
  );
}

export default StateGovSearchLinksPage;
